import React, { useState } from "react";
import { X, Clock, User, Calendar, Send, AlertTriangle, Link2 } from "lucide-react";
import type { OrderTodo, TodoNote, TodoStatus } from "../../../../domain/types/todo";
import {
  TODO_STATUS_LABELS,
  TODO_DEPENDENCY_TYPE_LABELS,
  ORDER_PRIORITY_LABELS,
} from "../../../../domain/types/todo";

interface TodoDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  todo: OrderTodo | null;
  onAddNote: (todoId: number, content: string) => Promise<void>;
  onStatusChange: (todo: OrderTodo, newStatus: TodoStatus) => Promise<void>;
  isLoading?: boolean;
}

export const TodoDetailDialog: React.FC<TodoDetailDialogProps> = ({
  isOpen,
  onClose,
  todo,
  onAddNote,
  onStatusChange,
  isLoading = false,
}) => {
  const [noteContent, setNoteContent] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  if (!isOpen || !todo) return null;

  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}분`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasBlockingDependency = todo.dependencies.some(
    (d) => d.dependencyType === "BLOCKING" && d.dependsOnTodoStatus !== "COMPLETED"
  );

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;

    setIsSubmittingNote(true);
    try {
      await onAddNote(todo.id, noteContent);
      setNoteContent("");
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const getStatusColorClass = (status: TodoStatus) => {
    switch (status) {
      case "PENDING":
        return "bg-gray-100 text-gray-700";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-700";
      case "WAITING":
        return "bg-yellow-100 text-yellow-700";
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const statusOptions: TodoStatus[] = ["PENDING", "IN_PROGRESS", "WAITING", "COMPLETED"];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between p-4 border-b flex-shrink-0">
            <div className="flex-1 min-w-0 pr-4">
              <h2 className="text-lg font-semibold text-gray-900">{todo.name}</h2>
              {todo.order && (
                <p className="text-sm text-gray-500 mt-1">
                  {todo.order.name} · {todo.order.orderNumber}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg flex-shrink-0"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Blocking Warning */}
            {hasBlockingDependency && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    선행 작업이 완료되지 않았습니다
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    이 작업을 시작하려면 선행 작업을 먼저 완료해야 합니다.
                  </p>
                </div>
              </div>
            )}

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                상태
              </label>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    onClick={() => onStatusChange(todo, status)}
                    disabled={
                      isLoading ||
                      todo.status === status ||
                      (hasBlockingDependency && status === "IN_PROGRESS")
                    }
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      todo.status === status
                        ? `${getStatusColorClass(status)} border-transparent font-medium`
                        : "bg-white border-gray-300 text-gray-700 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    }`}
                  >
                    {TODO_STATUS_LABELS[status]}
                  </button>
                ))}
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Priority */}
              {todo.order?.priority && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    우선순위
                  </label>
                  <p className="text-sm text-gray-900">
                    {ORDER_PRIORITY_LABELS[todo.order.priority]}
                  </p>
                </div>
              )}

              {/* Estimated Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  예상 소요 시간
                </label>
                <div className="flex items-center gap-1 text-sm text-gray-900">
                  <Clock size={14} />
                  {formatMinutes(todo.estimatedMinutes)}
                </div>
              </div>

              {/* Due Date */}
              {todo.order?.dueDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    마감일
                  </label>
                  <div className="flex items-center gap-1 text-sm text-gray-900">
                    <Calendar size={14} />
                    {new Date(todo.order.dueDate).toLocaleDateString("ko-KR")}
                  </div>
                </div>
              )}

              {/* Created At */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  생성일
                </label>
                <p className="text-sm text-gray-900">
                  {new Date(todo.createdAt).toLocaleDateString("ko-KR")}
                </p>
              </div>
            </div>

            {/* Description */}
            {todo.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  설명
                </label>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {todo.description}
                </p>
              </div>
            )}

            {/* Assignees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                담당자
              </label>
              {todo.assignees.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {todo.assignees.map((assignee) => (
                    <div
                      key={assignee.id}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg"
                    >
                      <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                        {assignee.userName?.charAt(0) || "?"}
                      </div>
                      <span className="text-sm text-gray-700">{assignee.userName}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">담당자가 지정되지 않았습니다.</p>
              )}
            </div>

            {/* Dependencies */}
            {todo.dependencies.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Link2 size={14} className="inline mr-1" />
                  의존성
                </label>
                <div className="space-y-2">
                  {todo.dependencies.map((dep) => (
                    <div
                      key={dep.id}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                        dep.dependencyType === "BLOCKING"
                          ? dep.dependsOnTodoStatus === "COMPLETED"
                            ? "bg-green-50 border-green-200"
                            : "bg-red-50 border-red-200"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          {dep.dependsOnTodoName}
                        </span>
                        <span
                          className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                            dep.dependencyType === "BLOCKING"
                              ? "bg-red-100 text-red-700"
                              : dep.dependencyType === "WARNING"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {TODO_DEPENDENCY_TYPE_LABELS[dep.dependencyType]}
                        </span>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          dep.dependsOnTodoStatus === "COMPLETED"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {TODO_STATUS_LABELS[dep.dependsOnTodoStatus]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                메모
              </label>

              {/* Note Input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="메모를 입력하세요..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                />
                <button
                  onClick={handleAddNote}
                  disabled={isSubmittingNote || !noteContent.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                </button>
              </div>

              {/* Notes List */}
              {todo.notes.length > 0 ? (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {todo.notes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-gray-50 rounded-lg p-3 border border-gray-100"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <User size={14} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">
                          {note.authorName}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDate(note.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {note.content}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">메모가 없습니다.</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoDetailDialog;
